'use client'

import {SanityImageType, VideoOrImageType} from '@local/types'
import {cx} from '@local/utils/cx'
import {
	Button,
	Locale,
	Pastil,
	SanityImage,
	SlideshowNative,
	SmallText,
	VideoLoop,
} from '@local/ui/components'
import {useEffect, useRef, useState} from 'react'
import {useMatchTouch} from '@local/hooks'

export interface ProductPreviewProps {
	store: {
		tags: string;
		keywords: string;
		productTag?: string;
		variants: Array<{ type: string }>;
		createdAt: string;
		priceRange: {
			minVariantPrice: string;
			maxVariantPrice: string;
		};
	};

	slug: string;
	slugPrefix: 'experience' | 'merch';
	excerpt?: string;
	location?: string;
	datePeriod?: string;
	productType?: string;
}

type TagTypes =
  | 'kaer-choice'
  | 'coming-soon'
  | 'fully-booked'
  | 'last-spots'
  | 'previous-experience';

export interface ProductGridCardProps {
	className?: string;
	media: VideoOrImageType;
	image?: SanityImageType;
	slug: string;
	item: ProductPreviewProps;
	id: string;
	tag?: TagTypes;
	keywords?: string;
	type?: string;
	disabled?: boolean;
	reviewAuthor?: string;
	reviewText?: string;
}

export const ProductSlider = ({items}: { items: ProductGridCardProps[] }) => {
	const slideshowRef = useRef(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const [slideCount, setSlideCount] = useState<number>(6)

	useEffect(() => {
		const handleResize = () => {
			const containerWidth = slideshowRef.current?.offsetWidth
			const childWidth = 400
			const itemCount = items.length
			const slidePerView = Math.floor(containerWidth / childWidth)
			const moveCount = itemCount - slidePerView + 1
			setSlideCount(moveCount)
		}

		handleResize()
		window.addEventListener('resize', handleResize)
	}, [])
	const [isHover, setIsHover] = useState(false)
	const handleMouseEnter = () => {
		if (videoRef.current) {
			videoRef.current.restart()
			videoRef.current.play()
		}
		setIsHover(true)
	}
	const handleMouseLeave = () => {
		if (videoRef.current) {
			videoRef.current.pause()
		}
		setIsHover(false)
	}
	const isTouch = useMatchTouch()

	if (!items.length) return <></>

	return (

		<SlideshowNative
			withPrevNextNavigation
			containerClassName='w-full relative px-20 hidden lg:block'
			className='flex flex-row gap-20'
			containerRef={slideshowRef}
			disableObserverOnMobile
			button={
				<Button variant='primary' className=' mt-10' href='/experiences'>
					<Locale>View All</Locale>
				</Button>
			}
			elCount={slideCount}
			prevNextButtonVariant='secondary'>
			{items.map((el, index) => {
				const {item, media, slug, keywords, tag} = el
				return (
					<div
						key={index}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						className='group overflow-hidden'>
						<Button
							href={slug}
							className='lg:w-[400px]  rounded-lg group/btn outline-none w-full h-full !relative !block'>
							<SanityImage
								image={media?.video?.poster || media?.image}
								width={200}
								height={300}
								className={cx(
									'w-full object-cover',
									'transition-transform  duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
									'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
								)} />
							{media?.video?.srcs && (
								<VideoLoop
									ref={videoRef}
									srcs={media.video.srcs}
									poster={media.video.poster}
									// preload={isTouch ? 'none' : 'metadata'}
									playing={isHover}
									ratio={1080 / 1920}
									posterDesktopWidth={1080}
									priority={!isTouch}
									className={cx(
										'absolute inset-0 h-full w-full object-cover  [&_video]:object-cover [&_video]:h-full',
										'transition-[transform,opacity] duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
										'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
										'opacity-0 group-hover:opacity-100 group-focus-visible/btn:scale-100',
									)} />
							)}
							<div
								className={cx(
									'!absolute inset-0 flex flex-col items-center justify-center text-white bg-black/25 px-12 lg:px-30',
									'transition-[backdrop-filter,background-color] transform-gpu duration-700 ease-quad-out',
									'group-hover:backdrop-blur-[2px] group-hover:bg-black/40',
									'group-focus-visible/btn:backdrop-blur-[2px] group-focus-visible/btn:bg-black/40',
								)}>
								<h3 className='font-serif capitalize text-16 lg:text-20 leading-snug mb-6'>
									{item?.title}
								</h3>

								<SmallText textColor='white'>
									<span>
										<Locale>Location</Locale>:
									</span>
									<span>{item.location}</span>
								</SmallText>

								<SmallText textColor='white'>{item.datePeriod}</SmallText>
							</div>
							{tag === 'kaer-choice' && (
								<Pastil className='absolute top-20 left-20'>
									<Locale>Kaer Choice</Locale>
								</Pastil>
							)}
							{tag === 'fully-booked' && (
								<Pastil className='absolute top-20 left-20'>
									<Locale>Fully Booked</Locale>
								</Pastil>
							)}
							{tag === 'last-spots' && (
								<Pastil className='absolute top-20 left-20'>
									<Locale>Last Spots</Locale>
								</Pastil>
							)}
							{tag === 'previous-experience' && (
								<Pastil variant='grey' className='absolute top-20 left-20'>
									<Locale>Previous Experience</Locale>
								</Pastil>
							)}
							{keywords &&
								<div className='absolute bottom-20 left-1/2 transform -translate-x-1/2'>
									<SmallText textColor='white'>
										<span className='whitespace-nowrap'>

											{keywords}
										</span>
									</SmallText>
								</div>}
						</Button>
					</div>
				)
			})}
		</SlideshowNative>

	)
}
